import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../../utils/api';
import { validators, validateTimeRange } from '../../utils/validation';
import { toast } from '../ui/sonner';
import ProgramLocationPicker from '../common/ProgramLocationPicker';
import PayRateGate from '../shared/PayRateGate';
import { usePlatformSettings } from '../../contexts/PlatformSettingsContext';
import {
  Briefcase, Sparkles, Clock, DollarSign, MapPin, Users, FileText,
  ChevronRight, ChevronLeft, X, RefreshCw, Copy, Wand2, Save, Send,
  Calendar, Tag, AlertCircle, CheckCircle, Award, Settings, Plus, Trash2,
  Pen, Type
} from 'lucide-react';

export default function EnhancedJobPosting({ onClose, onJobCreated, editJob = null, onDraftSaved = null }) {
  const [step, setStep] = useState(1);
  const [fieldErrors, setFieldErrors] = useState({});
  const [stepStatus, setStepStatus] = useState({}); // {1: 'ok'|'error'}
  const [dupWarning, setDupWarning] = useState(null);
  const [minPayPolicy, setMinPayPolicy] = useState(null);
  const { payRateEditingEnabled } = usePlatformSettings();
  const dupTimer = useRef(null);
  const [loading, setLoading] = useState(false);

  // iter83: Autosave — every ~20s of idle jobData changes, silently POST
  // the draft. Tracks last-saved timestamp for a visible "Draft saved Xs ago"
  // badge in the modal header.
  const [lastAutosaveAt, setLastAutosaveAt] = useState(null);
  const [autosaving, setAutosaving] = useState(false);
  const [autosaveTick, setAutosaveTick] = useState(0); // rerender for relative-time label
  const autosaveTimerRef = useRef(null);
  const firstAutosaveRunRef = useRef(true);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  
  const [jobData, setJobData] = useState({
    title: '',
    description: '',
    category: 'general',
    industry: '',
    job_type: 'shift', // shift, contract, project
    status: 'draft',
    
    // Pay configuration
    pay_type: 'hourly',
    hourly_rate: '',
    fixed_amount: '',
    budget_min: '',
    budget_max: '',
    
    // Schedule
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    is_recurring: false,
    recurrence_pattern: 'weekly',
    recurrence_days: [],
    recurrence_start_date: '',
    recurrence_end_date: '',
    
    // Location — iter77: optional program/location linkage. When set, the
    // form auto-fills address from the picked Location and stamps the job
    // with program_id/location_id so shift history + filtering stays intact.
    program_id: null,
    location_id: null,
    location: {
      address: '',
      city: '',
      state: '',
      zip: '',
      lat: null,
      lng: null,
      radius_meters: 100,
      is_remote: false
    },
    
    // Requirements
    required_skills: [],
    required_certifications: [],
    experience_level: 'entry',
    workers_needed: 1,
    
    // Additional
    requirements: '',
    dress_code: '',
    equipment_provided: '',
    break_policy: '',
    special_instructions: '',
    
    // Shift Tasks
    shift_tasks: [],
    allow_photo_uploads: false,
    
    // Task Verification
    task_verification: {
      manager_name: '',
      manager_signature: '',
      requirement_accepted: null, // null = not decided, true = accepted, false = denied
      verified_at: null
    },
    
    // SLA
    sla_hours: 24,
    auto_match: true
  });

  const [aiPrompt, setAiPrompt] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const [certInput, setCertInput] = useState('');
  const [taskInput, setTaskInput] = useState('');
  
  // E-Signature states
  const [signatureMode, setSignatureMode] = useState('type'); // 'type' or 'draw'
  const [typedSignature, setTypedSignature] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  // Canvas drawing functions
  const getCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    
    // Calculate the scale factor between displayed size and internal canvas size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Apply scale to get correct coordinates
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    return { x, y };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const { x, y } = getCanvasCoordinates(e, canvas);
    setIsDrawing(true);
    setLastPos({ x, y });
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e, canvas);
    
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    
    setLastPos({ x, y });
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL();
      setJobData({
        ...jobData,
        task_verification: {
          ...jobData.task_verification,
          manager_signature: signatureData,
          requirement_accepted: null
        }
      });
    }
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setJobData({
      ...jobData,
      task_verification: {
        ...jobData.task_verification,
        manager_signature: '',
        requirement_accepted: null
      }
    });
  };

  // If editing a job (or continuing a draft), merge its data into the form.
  // Iter79 fix: some drafts save `location: null` or `location: {}` — a naive
  // spread `...editJob` wipes out the default nested `location` object and
  // later `jobData.location.address` NPEs. Deep-merge nested objects and skip
  // null/undefined top-level keys.
  useEffect(() => {
    if (editJob) {
      setJobData((prev) => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(editJob)) {
          if (v === null || v === undefined) continue;
          if (k === 'location' && typeof v === 'object' && !Array.isArray(v)) {
            next.location = { ...prev.location, ...v };
          } else if (k === 'task_verification' && typeof v === 'object' && !Array.isArray(v)) {
            next.task_verification = { ...prev.task_verification, ...v };
          } else if ((k === 'start_time' || k === 'end_time') && typeof v === 'string' && v.includes('T')) {
            // Draft stores full ISO — strip to bare HH:MM for <input type="time">
            next[k] = v.slice(11, 16);
            if (!prev.start_date && k === 'start_time') {
              next.start_date = v.slice(0, 10);
            }
          } else {
            next[k] = v;
          }
        }
        return next;
      });
    }
    loadTemplates();
  }, [editJob]);

  const loadTemplates = async () => {
    try {
      const response = await apiCall('/api/employer/job-templates');
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please describe the job you want to create');
      return;
    }

    setAiGenerating(true);
    try {
      const response = await apiCall('/api/employer/jobs/ai-generate', {
        method: 'POST',
        body: JSON.stringify({ prompt: aiPrompt })
      });
      
      if (response.job_data) {
        setJobData(prev => ({
          ...prev,
          ...response.job_data,
          required_skills: response.job_data.required_skills || [],
          required_certifications: response.job_data.required_certifications || []
        }));
        toast.success('Job details generated! Review and customize as needed.');
        setAiPrompt('');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to generate job details');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleUseTemplate = (template) => {
    setJobData(prev => ({
      ...prev,
      title: template.title,
      description: template.description,
      category: template.category,
      industry: template.industry,
      required_skills: template.required_skills || [],
      required_certifications: template.required_certifications || [],
      hourly_rate: template.hourly_rate || '',
      requirements: template.requirements || '',
      dress_code: template.dress_code || ''
    }));
    setShowTemplates(false);
    toast.success('Template applied!');
  };

  const handleSaveTemplate = async () => {
    try {
      await apiCall('/api/employer/job-templates', {
        method: 'POST',
        body: JSON.stringify({
          name: jobData.title,
          ...jobData
        })
      });
      toast.success('Template saved!');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleAddSkill = () => {
    if (skillInput.trim() && !jobData.required_skills.includes(skillInput.trim())) {
      setJobData(prev => ({
        ...prev,
        required_skills: [...prev.required_skills, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const handleAddCertification = () => {
    if (certInput.trim() && !jobData.required_certifications.includes(certInput.trim())) {
      setJobData(prev => ({
        ...prev,
        required_certifications: [...prev.required_certifications, certInput.trim()]
      }));
      setCertInput('');
    }
  };

  const handleSaveDraft = async () => {
    if (!jobData.title) {
      toast.error('Please provide a job title to save draft');
      return;
    }

    setLoading(true);
    try {
      await _persistDraft();
      // iter82: "Save & Exit" — offer a one-tap CTA to jump to the Drafts
      // sub-tab so the flow feels intentional. If the parent didn't pass
      // onDraftSaved we fall back to a plain success toast.
      if (onDraftSaved) {
        toast.success('Draft saved!', {
          description: 'You can pick this back up any time from Draft Jobs.',
          action: {
            label: 'View Drafts →',
            onClick: () => {
              onDraftSaved();
              onClose && onClose();
            },
          },
          duration: 6000,
        });
      } else {
        toast.success('Draft saved!');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  // Extracted for reuse by autosave. Silent (no toast) on success/failure —
  // the "Draft saved Xs ago" badge in the header is the only UX feedback.
  const _persistDraft = async () => {
    // Combine date + time into full ISO strings so the backend's Pydantic
    // datetime validator accepts them (was rejecting bare "08:00" as too-short).
    // Fall back to a safe default when the user hasn't picked a start_date yet.
    const today = new Date().toISOString().slice(0, 10);
    const startDate = jobData.start_date || today;
    const endDate = jobData.end_date || startDate;
    const startTime = jobData.start_time || '09:00';
    const endTime = jobData.end_time || '17:00';
    const startDateTime = `${startDate}T${startTime}:00`;
    const endDateTime = `${endDate}T${endTime}:00`;

    const payload = {
      title: jobData.title,
      description: jobData.description || '',
      category: jobData.category,
      industry: jobData.industry || jobData.category,
      pay_rate: parseFloat(jobData.hourly_rate) || parseFloat(jobData.fixed_amount) || 0,
      start_date: jobData.start_date,
      end_date: jobData.end_date,
      start_time: startDateTime,
      end_time: endDateTime,
      location: jobData.location,
      program_id: jobData.program_id || null,
      location_id: jobData.location_id || null,
      required_skills: jobData.required_skills || [],
      required_certifications: jobData.required_certifications || [],
      experience_level: jobData.experience_level,
      workers_needed: jobData.workers_needed,
      requirements: jobData.requirements,
      dress_code: jobData.dress_code,
      status: 'draft',
    };
    await apiCall('/api/jobs/drafts', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    setLastAutosaveAt(Date.now());
  };

  // Debounced autosave: whenever jobData changes and the title is present,
  // schedule a background save 20s later. Any subsequent edit resets the
  // timer, so we don't hammer the API mid-typing.
  useEffect(() => {
    // Skip the very first render (initial jobData construction) so we don't
    // autosave an empty form immediately on modal open.
    if (firstAutosaveRunRef.current) {
      firstAutosaveRunRef.current = false;
      return;
    }
    if (!jobData.title) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      setAutosaving(true);
      try {
        await _persistDraft();
      } catch (_) {
        // Silent — UI just shows the last successful time.
      } finally {
        setAutosaving(false);
      }
    }, 20000);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
     
  }, [jobData]);

  // Rerender the "Xs ago" label every 5s so it stays fresh.
  useEffect(() => {
    if (!lastAutosaveAt) return;
    const t = setInterval(() => setAutosaveTick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, [lastAutosaveAt]);

  const _formatSavedAgo = () => {
    if (!lastAutosaveAt) return null;
    const seconds = Math.floor((Date.now() - lastAutosaveAt) / 1000);
    if (seconds < 5) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ago`;
  };
  // Reference autosaveTick so React knows this component uses it for
  // re-render timing (no-op operationally).
  void autosaveTick;

  const handlePublish = async () => {
    // Centralized validation (mirrors backend validators.py)
    if (!jobData.title || !jobData.description) {
      toast.error('Please fill in job title and description');
      return;
    }
    const titleErr = validators.jobTitle(jobData.title);
    if (titleErr) {
      toast.error(titleErr);
      return;
    }
    const descErr = validators.description(jobData.description);
    if (descErr) {
      toast.error(descErr);
      return;
    }
    if (payRateEditingEnabled && !jobData.hourly_rate && !jobData.fixed_amount) {
      toast.error('Please set pay rate');
      return;
    }
    const rateErr = payRateEditingEnabled ? validators.rate(jobData.hourly_rate || jobData.fixed_amount) : null;
    if (rateErr) {
      toast.error(`Pay rate: ${rateErr}`);
      return;
    }
    if (!jobData.start_date) {
      toast.error('Please set start date');
      return;
    }
    if (validators.date(jobData.start_date) || (jobData.end_date && validators.date(jobData.end_date))) {
      toast.error('Invalid date.');
      return;
    }
    {
      const s = `${jobData.start_date}T${jobData.start_time || '09:00'}:00`;
      const e = jobData.end_date
        ? `${jobData.end_date}T${jobData.end_time || '17:00'}:00`
        : `${jobData.start_date}T${jobData.end_time || '17:00'}:00`;
      const rangeErr = validateTimeRange(s, e, 'Shift end time');
      if (rangeErr) {
        toast.error(rangeErr);
        return;
      }
    }

    setLoading(true);
    try {
      // Format data for the backend
      const startDateTime = jobData.start_date && jobData.start_time 
        ? `${jobData.start_date}T${jobData.start_time || '09:00'}:00`
        : `${jobData.start_date}T09:00:00`;
      
      const endDateTime = jobData.end_date && jobData.end_time
        ? `${jobData.end_date}T${jobData.end_time || '17:00'}:00`
        : jobData.start_date && jobData.end_time
          ? `${jobData.start_date}T${jobData.end_time}:00`
          : `${jobData.start_date}T17:00:00`;

      const payload = {
        title: jobData.title,
        description: jobData.description,
        description_rich: jobData.description,
        category: jobData.category,
        industry: jobData.industry || jobData.category,
        pay_rate: payRateEditingEnabled ? (parseFloat(jobData.hourly_rate) || parseFloat(jobData.fixed_amount) || null) : null,
        currency: 'USD',
        start_time: startDateTime,
        end_time: endDateTime,
        location: {
          lat: jobData.location.lat || 37.7749,
          lng: jobData.location.lng || -122.4194,
          address: `${jobData.location.address || ''} ${jobData.location.city || ''}`.trim() || 'Remote',
          radius_meters: jobData.location.radius_meters || 100
        },
        // iter77: linkage to Group Homes Program + Location (both required
        // together; backend auto-fills location.address if provided).
        program_id: jobData.program_id || null,
        location_id: jobData.location_id || null,
        required_skills: jobData.required_skills || [],
        certifications_required: jobData.required_certifications || [],
        experience_level: jobData.experience_level || 'entry',
        compensation_type: jobData.pay_type || 'hourly',
        requirements: jobData.requirements || ''
      };

      const response = await apiCall('/api/jobs', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      toast.success('Job published successfully!');
      onJobCreated?.(response.job);
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to publish job');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { id: 1, title: 'Basic Info', icon: <Briefcase className="w-5 h-5" /> },
    { id: 2, title: 'Pay & Schedule', icon: <Calendar className="w-5 h-5" /> },
    { id: 3, title: 'Requirements', icon: <Award className="w-5 h-5" /> },
    { id: 4, title: 'Review', icon: <CheckCircle className="w-5 h-5" /> }
  ];

  // ---- Per-step validation (June 2026 UAT fix: validate at each tab, not
  // only at final submission). Returns {field: message}.
  const validateStep = (n) => {
    const errs = {};
    if (n === 1) {
      if (!jobData.title?.trim()) errs.title = 'Job title is required.';
      else {
        const e = validators.jobTitle(jobData.title);
        if (e) errs.title = e;
      }
      if (!jobData.description?.trim()) errs.description = 'Job description is required.';
      else {
        const e = validators.description(jobData.description);
        if (e) errs.description = e;
      }
      if ((jobData.program_id && !jobData.location_id) || (!jobData.program_id && jobData.location_id)) {
        errs.program = 'Select both a Program and a Location.';
      }
      // iter159: free-text address is gone — Program + Location are the
      // only way to attach a shift to a place. Remote shifts skip the
      // requirement.
      if (!jobData.location?.is_remote && (!jobData.program_id || !jobData.location_id)) {
        errs.program = errs.program || 'Pick a Program and Location for this shift. Create one in the Locations tab if you have none yet.';
      }
    }
    if (n === 2) {
      if (!jobData.start_date) errs.start_date = 'Start date is required.';
      else if (validators.date(jobData.start_date)) errs.start_date = 'Start date is invalid.';
      if (jobData.end_date && validators.date(jobData.end_date)) errs.end_date = 'End date is invalid.';
      if (jobData.start_date) {
        const s = `${jobData.start_date}T${jobData.start_time || '09:00'}:00`;
        const e = `${jobData.end_date || jobData.start_date}T${jobData.end_time || '17:00'}:00`;
        const rangeErr = validateTimeRange(s, e, 'Shift end time');
        if (rangeErr) errs.end_time = rangeErr;
      }
      const rate = jobData.pay_type === 'fixed' ? jobData.fixed_amount : jobData.hourly_rate;
      if (payRateEditingEnabled) {
        if (!rate) errs.rate = 'Pay rate is required.';
        else {
          const e = validators.rate(rate);
          if (e) errs.rate = e;
        }
      }
      if (
        payRateEditingEnabled &&
        jobData.pay_type === 'hourly' &&
        minPayPolicy?.enforcement === 'block' &&
        Number(rate) > 0 &&
        Number(rate) < Number(minPayPolicy.platform_min_rate)
      ) {
        errs.rate = `Hourly rate must be at least $${minPayPolicy.platform_min_rate}/hr (platform minimum).`;
      }
    }
    return errs;
  };

  const goToStep = (target) => {
    if (target <= step) {
      setStep(target);
      return;
    }
    // Validate every step between current and target before jumping forward.
    for (let n = step; n < target; n++) {
      const errs = validateStep(n);
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        setStepStatus(prev => ({ ...prev, [n]: 'error' }));
        setStep(n);
        toast.error(Object.values(errs)[0]);
        return;
      }
      setStepStatus(prev => ({ ...prev, [n]: 'ok' }));
    }
    setFieldErrors({});
    setStep(target);
  };

  const clearFieldError = (field) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const checkDuplicateTitle = (title) => {
    if (dupTimer.current) clearTimeout(dupTimer.current);
    if (!title || title.trim().length < 4) { setDupWarning(null); return; }
    dupTimer.current = setTimeout(async () => {
      try {
        const res = await apiCall(`/api/employer/jobs/check-duplicate?title=${encodeURIComponent(title.trim())}`);
        setDupWarning(res.duplicate ? res : null);
      } catch (_e) { setDupWarning(null); }
    }, 600);
  };

  const FieldError = ({ field }) => fieldErrors[field] ? (
    <p className="text-xs text-red-600 mt-1 font-medium" data-testid={`field-error-${field}`}>{fieldErrors[field]}</p>
  ) : null;

  useEffect(() => {
    apiCall('/api/platform/min-pay').then(setMinPayPolicy).catch(() => {});
  }, []);

  const belowPlatformMin =
    jobData.pay_type === 'hourly' &&
    Number(jobData.hourly_rate) > 0 &&
    minPayPolicy &&
    Number(jobData.hourly_rate) < Number(minPayPolicy.platform_min_rate);

  const errCls = (field) => fieldErrors[field] ? 'border-red-400 ring-1 ring-red-300' : 'border-slate-300';


  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[96vh] sm:max-h-[92vh] flex flex-col overflow-hidden" data-testid="job-wizard-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-4 sm:p-6 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <Briefcase className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{editJob ? 'Edit Job' : 'Post New Job'}</h2>
                <p className="text-white/80">Create a job posting with AI assistance</p>
              </div>
            </div>
            {/* iter83: Autosave indicator + close */}
            <div className="flex items-center gap-3">
              {(autosaving || lastAutosaveAt) && (
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 backdrop-blur-sm rounded-full text-xs font-medium text-white/90 border border-white/20"
                  data-testid="autosave-indicator"
                  title="Your work is being saved automatically every ~20s"
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${autosaving ? 'bg-amber-300 animate-pulse' : 'bg-emerald-300'}`} />
                  {autosaving ? 'Saving…' : `Draft saved ${_formatSavedAgo()}`}
                </div>
              )}
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mt-6 px-4">
            {steps.map((s, idx) => (
              <React.Fragment key={s.id}>
                <button
                  onClick={() => goToStep(s.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition ${
                    step === s.id
                      ? 'bg-white/30 text-white'
                      : step > s.id
                      ? 'bg-white/10 text-white/80'
                      : 'text-white/50'
                  }`}
                  data-testid={`job-step-${s.id}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    stepStatus[s.id] === 'error' ? 'bg-red-500/60' : step >= s.id ? 'bg-white/20' : 'bg-white/10'
                  }`}>
                    {stepStatus[s.id] === 'error' ? <AlertCircle className="w-5 h-5" />
                      : (step > s.id || stepStatus[s.id] === 'ok') ? <CheckCircle className="w-5 h-5" /> : s.icon}
                  </div>
                  <span className="hidden md:inline font-medium">{s.title}</span>
                </button>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${step > s.id ? 'bg-white/40' : 'bg-white/10'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* AI Assistant Bar - Compact version */}
        <div className="bg-gradient-to-r from-violet-50 to-purple-50 border-b border-purple-100 p-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <span className="font-semibold text-purple-900 text-sm">AI Job Assistant</span>
            <input
              type="text"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Describe the job (e.g., 'Need a healthcare worker for night shifts')"
              className="flex-1 px-3 py-2 bg-white border border-purple-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
              onKeyPress={(e) => e.key === 'Enter' && handleAIGenerate()}
              data-testid="ai-prompt-input"
            />
            <button
              onClick={handleAIGenerate}
              disabled={aiGenerating}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium text-sm hover:shadow-lg transition disabled:opacity-50 flex items-center gap-1.5"
              data-testid="ai-generate-btn"
            >
              {aiGenerating ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4" />
              )}
              Generate
            </button>
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="px-3 py-2 bg-white border border-purple-200 text-purple-700 rounded-lg font-medium text-sm hover:bg-purple-50 transition flex items-center gap-1.5"
            >
              <Copy className="w-4 h-4" />
              Templates
            </button>
          </div>

          {/* Templates Dropdown */}
          {showTemplates && templates.length > 0 && (
            <div className="mt-2 p-2 bg-white rounded-lg border border-purple-200 max-h-40 overflow-y-auto">
              {templates.map((template, idx) => (
                <button
                  key={idx}
                  onClick={() => handleUseTemplate(template)}
                  className="w-full text-left p-2 hover:bg-purple-50 rounded-lg transition"
                >
                  <p className="font-medium text-gray-900 text-sm">{template.name || template.title}</p>
                  <p className="text-xs text-gray-500">{template.category} • ${template.hourly_rate}/hr</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0" data-testid="job-wizard-body">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Job Title *</label>
                  <input
                    type="text"
                    value={jobData.title}
                    onChange={(e) => {
                      setJobData({ ...jobData, title: e.target.value.slice(0, 100) });
                      clearFieldError('title');
                      checkDuplicateTitle(e.target.value);
                    }}
                    maxLength={100}
                    className={`w-full px-4 py-3 bg-white border ${errCls('title')} rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition`}
                    placeholder="e.g., Registered Nurse - Night Shift"
                    data-testid="job-title-input"
                  />
                  <FieldError field="title" />
                  {dupWarning && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 mt-1" data-testid="duplicate-title-warning">
                      ⚠ A similar {dupWarning.existing_status === 'draft' ? 'draft' : 'active'} job "{dupWarning.existing_title}" already exists. Review it before creating a duplicate.
                    </p>
                  )}
                  <p className={`text-xs mt-1 ${(jobData.title || '').length >= 90 ? 'text-amber-600 font-medium' : 'text-slate-400'}`} data-testid="job-title-char-count">
                    {(jobData.title || '').length}/100 characters
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Category *</label>
                  <select
                    value={jobData.category}
                    onChange={(e) => setJobData({ ...jobData, category: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition appearance-none cursor-pointer"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                  >
                    <option value="general">General Labor</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="hospitality">Hospitality</option>
                    <option value="retail">Retail</option>
                    <option value="warehouse">Warehouse</option>
                    <option value="events">Events</option>
                    <option value="security">Security</option>
                    <option value="cleaning">Cleaning</option>
                    <option value="administrative">Administrative</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Job Type</label>
                  <select
                    value={jobData.job_type}
                    onChange={(e) => setJobData({ ...jobData, job_type: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition appearance-none cursor-pointer"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                  >
                    <option value="shift">Single Shift</option>
                    <option value="recurring">Recurring Shifts</option>
                    <option value="contract">Contract</option>
                    <option value="project">Project-Based</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Job Description *</label>
                <textarea
                  value={jobData.description}
                  onChange={(e) => { setJobData({ ...jobData, description: e.target.value }); clearFieldError('description'); }}
                  className={`w-full px-4 py-3 bg-white border ${errCls('description')} rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition resize-none`}
                  rows={5}
                  placeholder="Describe the job responsibilities, expectations, and any important details..."
                />
                <FieldError field="description" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Location</label>
                {/* iter159 (Feb 2026): free-text address inputs removed —
                    every shift now inherits its full address from a
                    reusable Location created in the Employer → Locations
                    tab. Keeps posted shifts in-sync with location-level
                    data (radius, coordinates, address components) and
                    prevents typos that break geo checks. */}
                <ProgramLocationPicker
                  value={{ program_id: jobData.program_id, location_id: jobData.location_id }}
                  onChange={({ program_id, location_id, location }) => {
                    const addr = location?.address || {};
                    const isDict = addr && typeof addr === 'object';
                    setJobData((prev) => ({
                      ...prev,
                      program_id,
                      location_id,
                      location: {
                        ...prev.location,
                        address: isDict ? (addr.street || '') : (typeof addr === 'string' ? addr : ''),
                        city: isDict ? (addr.city || '') : '',
                        state: isDict ? (addr.state || '') : '',
                        zip: isDict ? (addr.zip || '') : '',
                      },
                    }));
                    clearFieldError('address');
                    clearFieldError('program');
                  }}
                  testIdPrefix="employer-post-shift-picker"
                  colorScheme="indigo"
                  required
                />
                <FieldError field="address" />
                <FieldError field="program" />
                {jobData.location_id && (
                  <p className="text-xs text-slate-500 mt-2">
                    Shift address inherits from the selected location. Manage or add locations in the Employer → Locations tab.
                  </p>
                )}
                <div className="flex items-center gap-2 mt-3">
                  <input
                    type="checkbox"
                    id="is_remote"
                    checked={jobData.location.is_remote}
                    onChange={(e) => setJobData({ 
                      ...jobData, 
                      location: { ...jobData.location, is_remote: e.target.checked }
                    })}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <label htmlFor="is_remote" className="text-sm text-gray-600">This is a remote position</label>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Schedule & Pay */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Pay Configuration */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                <h4 className="font-semibold text-emerald-900 mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Pay Configuration
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Pay Type</label>
                    <select
                      value={jobData.pay_type}
                      onChange={(e) => setJobData({ ...jobData, pay_type: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition appearance-none cursor-pointer"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                    >
                      <option value="hourly">Hourly Rate</option>
                      <option value="fixed">Fixed Amount</option>
                      <option value="range">Budget Range</option>
                    </select>
                  </div>
                  
                  {jobData.pay_type === 'hourly' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Hourly Rate ($){payRateEditingEnabled ? ' *' : ''}</label>
                      <PayRateGate>
                      <input
                        type="number"
                        value={jobData.hourly_rate}
                        onChange={(e) => { setJobData({ ...jobData, hourly_rate: e.target.value }); clearFieldError('rate'); }}
                        className={`w-full px-4 py-3 bg-white border ${errCls('rate')} rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400`}
                        placeholder="45"
                        min="0"
                        step="0.01"
                      />
                      </PayRateGate>
                      {!payRateEditingEnabled && (
                        <p className="text-xs text-slate-500 mt-1" data-testid="pay-rate-locked-hint">Pay rate is managed by your administrator — not required to post this shift.</p>
                      )}
                      <FieldError field="rate" />
                      {belowPlatformMin && !fieldErrors.rate && (
                        <p
                          className={`text-xs mt-1 font-medium rounded-lg px-2 py-1 border ${
                            minPayPolicy?.enforcement === 'block'
                              ? 'text-red-700 bg-red-50 border-red-200'
                              : 'text-amber-700 bg-amber-50 border-amber-200'
                          }`}
                          data-testid="below-min-pay-warning"
                        >
                          {minPayPolicy?.enforcement === 'block'
                            ? `This rate is below the platform minimum of $${minPayPolicy.platform_min_rate}/hr — raise it to publish this job.`
                            : `Heads up: this rate is below the platform minimum of $${minPayPolicy.platform_min_rate}/hr. Workers will see a "Below Minimum Pay" alert on this job.`}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {jobData.pay_type === 'fixed' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Fixed Amount ($)</label>
                      <PayRateGate>
                      <input
                        type="number"
                        value={jobData.fixed_amount}
                        onChange={(e) => setJobData({ ...jobData, fixed_amount: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                        placeholder="500"
                        min="0"
                        step="0.01"
                      />
                      </PayRateGate>
                    </div>
                  )}
                  
                  {jobData.pay_type === 'range' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Budget Min ($)</label>
                        <input
                          type="number"
                          value={jobData.budget_min}
                          onChange={(e) => setJobData({ ...jobData, budget_min: e.target.value })}
                          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                          placeholder="200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Budget Max ($)</label>
                        <input
                          type="number"
                          value={jobData.budget_max}
                          onChange={(e) => setJobData({ ...jobData, budget_max: e.target.value })}
                          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                          placeholder="500"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Schedule */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <h4 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Schedule
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                    <input
                      type="date"
                      value={jobData.start_date}
                      onChange={(e) => { setJobData({ ...jobData, start_date: e.target.value }); clearFieldError('start_date'); clearFieldError('end_time'); }}
                      className={`w-full px-4 py-3 border ${errCls('start_date')} rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900`}
                    />
                    <FieldError field="start_date" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={jobData.end_date}
                      onChange={(e) => { setJobData({ ...jobData, end_date: e.target.value }); clearFieldError('end_date'); clearFieldError('end_time'); }}
                      className={`w-full px-4 py-3 border ${errCls('end_date')} rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900`}
                    />
                    <FieldError field="end_date" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                    <input
                      type="time"
                      value={jobData.start_time}
                      onChange={(e) => { setJobData({ ...jobData, start_time: e.target.value }); clearFieldError('end_time'); }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                    <input
                      type="time"
                      value={jobData.end_time}
                      onChange={(e) => { setJobData({ ...jobData, end_time: e.target.value }); clearFieldError('end_time'); }}
                      className={`w-full px-4 py-3 border ${errCls('end_time')} rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900`}
                    />
                    <FieldError field="end_time" />
                  </div>
                </div>
                
                <div className="mt-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_recurring"
                      checked={jobData.is_recurring}
                      onChange={(e) => setJobData({ ...jobData, is_recurring: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600"
                    />
                    <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700">This is a recurring shift</label>
                  </div>
                  
                  {jobData.is_recurring && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Recurrence Pattern</label>
                        <select
                          value={jobData.recurrence_pattern}
                          onChange={(e) => setJobData({ ...jobData, recurrence_pattern: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="biweekly">Bi-Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Start Recurring</label>
                          <input
                            type="date"
                            value={jobData.recurrence_start_date}
                            onChange={(e) => setJobData({ ...jobData, recurrence_start_date: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">End Recurring</label>
                          <input
                            type="date"
                            value={jobData.recurrence_end_date}
                            onChange={(e) => setJobData({ ...jobData, recurrence_end_date: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                      
                      <p className="text-xs text-blue-600">
                        The shift will repeat {jobData.recurrence_pattern} between the start and end dates.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Workers Needed</label>
                <input
                  type="number"
                  value={jobData.workers_needed}
                  onChange={(e) => setJobData({ ...jobData, workers_needed: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 text-gray-900"
                  min="1"
                  max="100"
                />
              </div>
            </div>
          )}

          {/* Step 3: Requirements */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Skills */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Required Skills</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSkill())}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 text-gray-900 placeholder-gray-400"
                    placeholder="Add a skill..."
                  />
                  <button
                    onClick={handleAddSkill}
                    className="px-4 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {jobData.required_skills.map((skill, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-sm"
                    >
                      {skill}
                      <button 
                        onClick={() => setJobData(prev => ({
                          ...prev,
                          required_skills: prev.required_skills.filter((_, i) => i !== idx)
                        }))}
                        className="hover:text-indigo-900"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Certifications */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Required Certifications</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={certInput}
                    onChange={(e) => setCertInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCertification())}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 text-gray-900 placeholder-gray-400"
                    placeholder="e.g., CPR, First Aid, RN License..."
                  />
                  <button
                    onClick={handleAddCertification}
                    className="px-4 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {jobData.required_certifications.map((cert, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm"
                    >
                      <Award className="w-4 h-4" />
                      {cert}
                      <button 
                        onClick={() => setJobData(prev => ({
                          ...prev,
                          required_certifications: prev.required_certifications.filter((_, i) => i !== idx)
                        }))}
                        className="hover:text-amber-900"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Experience Level */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Experience Level</label>
                <select
                  value={jobData.experience_level}
                  onChange={(e) => setJobData({ ...jobData, experience_level: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 text-gray-900"
                >
                  <option value="entry">Entry Level</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="experienced">Experienced</option>
                  <option value="expert">Expert</option>
                </select>
              </div>

              {/* Additional Requirements */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Requirements</label>
                <textarea
                  value={jobData.requirements}
                  onChange={(e) => setJobData({ ...jobData, requirements: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 text-gray-900 placeholder-gray-400 resize-none"
                  rows={3}
                  placeholder="Any other requirements or special instructions..."
                />
              </div>

              {/* Shift Tasks */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Shift Tasks</label>
                  <p className="text-sm text-gray-500 mb-3">Add tasks that workers must complete during this shift. Workers will check off completed tasks.</p>
                </div>
                
                {/* Add Task Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={taskInput}
                    onChange={(e) => setTaskInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && taskInput.trim()) {
                        e.preventDefault();
                        const currentTasks = jobData.shift_tasks || [];
                        setJobData({
                          ...jobData,
                          shift_tasks: [...currentTasks, { id: Date.now(), text: taskInput.trim(), completed: false }]
                        });
                        setTaskInput('');
                      }
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 text-gray-900 placeholder-gray-400"
                    placeholder="Enter a task (e.g., Complete patient rounds, Document vitals...)"
                    style={{ color: '#111827' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (taskInput.trim()) {
                        const currentTasks = jobData.shift_tasks || [];
                        setJobData({
                          ...jobData,
                          shift_tasks: [...currentTasks, { id: Date.now(), text: taskInput.trim(), completed: false }]
                        });
                        setTaskInput('');
                      }
                    }}
                    className="px-4 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>

                {/* Task List */}
                {(jobData.shift_tasks || []).length > 0 && (
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-700">Tasks to Complete ({jobData.shift_tasks.length})</p>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {jobData.shift_tasks.map((task, index) => (
                        <div key={task.id || index} className="flex items-center gap-3 p-4 hover:bg-gray-50">
                          <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-medium flex-shrink-0">
                            {index + 1}
                          </span>
                          <span className="flex-1 text-gray-700">
                            {task.text}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setJobData({
                                ...jobData,
                                shift_tasks: (jobData.shift_tasks || []).filter((_, i) => i !== index)
                              });
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                            title="Remove task"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {(jobData.shift_tasks || []).length === 0 && (
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
                    <p className="text-gray-500 text-sm">No tasks added yet. Add tasks above that workers need to complete during this shift.</p>
                  </div>
                )}

                {/* Allow Photo Uploads Option */}
                <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="allow_photo_uploads"
                      checked={jobData.allow_photo_uploads || false}
                      onChange={(e) => setJobData({ ...jobData, allow_photo_uploads: e.target.checked })}
                      className="w-5 h-5 mt-0.5 rounded border-gray-300 text-indigo-600"
                    />
                    <div className="flex-1">
                      <label htmlFor="allow_photo_uploads" className="text-sm font-medium text-indigo-900 cursor-pointer">
                        Allow Workers to Upload Photos/Images
                      </label>
                      <p className="text-sm text-indigo-700 mt-1">
                        When enabled, workers can attach photos as proof of task completion. 
                        You will see these images in the Tasks Needing Review section.
                      </p>
                      {jobData.allow_photo_uploads && (
                        <div className="mt-2 p-2 bg-white rounded-lg border border-indigo-200">
                          <p className="text-xs text-indigo-600 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Workers can upload up to 5 images per task submission
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="p-5 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Task Verification Required</p>
                      <p className="text-sm text-amber-700 mt-1">
                        A staff member from the program or assigned work location must verify if all tasks are completed. 
                        The verifying staff will be required to provide their <strong>name</strong> and <strong>signature</strong> on the worker's portal to confirm task completion.
                      </p>
                    </div>
                  </div>
                  
                  {/* Manager Acknowledgment Section */}
                  <div className="mt-4 pt-4 border-t border-amber-200">
                    <p className="text-sm font-semibold text-amber-900 mb-3">Manager Acknowledgment</p>
                    
                    {/* Status Badge */}
                    {jobData.task_verification?.requirement_accepted !== null && (
                      <div className={`mb-4 p-3 rounded-lg ${
                        jobData.task_verification.requirement_accepted 
                          ? 'bg-green-100 border border-green-300' 
                          : 'bg-red-100 border border-red-300'
                      }`}>
                        <p className={`text-sm font-medium ${
                          jobData.task_verification.requirement_accepted 
                            ? 'text-green-800' 
                            : 'text-red-800'
                        }`}>
                          {jobData.task_verification.requirement_accepted 
                            ? '✓ Task verification requirement ACCEPTED' 
                            : '✗ Task verification requirement DENIED'}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          Signed by: {jobData.task_verification.manager_name}
                        </p>
                      </div>
                    )}
                    
                    {/* Name and Signature Fields */}
                    <div className="space-y-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-amber-800 mb-1">Manager Name *</label>
                        <input
                          type="text"
                          value={jobData.task_verification?.manager_name || ''}
                          onChange={(e) => setJobData({
                            ...jobData,
                            task_verification: {
                              ...jobData.task_verification,
                              manager_name: e.target.value,
                              requirement_accepted: null
                            }
                          })}
                          disabled={jobData.task_verification?.requirement_accepted !== null}
                          className={`w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white text-gray-900 ${
                            jobData.task_verification?.requirement_accepted !== null ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          placeholder="Enter your full name"
                          style={{ color: '#111827' }}
                        />
                      </div>
                      
                      {/* E-Signature Section */}
                      <div>
                        <label className="block text-sm font-medium text-amber-800 mb-2">E-Signature *</label>
                        
                        {/* Show locked signature if already accepted/denied */}
                        {jobData.task_verification?.requirement_accepted !== null ? (
                          <div className="space-y-2">
                            <div className={`p-4 rounded-lg border-2 ${
                              jobData.task_verification.requirement_accepted 
                                ? 'bg-green-50 border-green-300' 
                                : 'bg-red-50 border-red-300'
                            }`}>
                              <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className={`w-4 h-4 ${
                                  jobData.task_verification.requirement_accepted ? 'text-green-600' : 'text-red-600'
                                }`} />
                                <span className={`text-sm font-medium ${
                                  jobData.task_verification.requirement_accepted ? 'text-green-800' : 'text-red-800'
                                }`}>
                                  Signature Saved & Locked
                                </span>
                              </div>
                              {/* Display saved signature */}
                              {jobData.task_verification.manager_signature?.startsWith('data:') ? (
                                <img 
                                  src={jobData.task_verification.manager_signature} 
                                  alt="Saved signature" 
                                  className="max-h-16 border border-gray-200 rounded bg-white p-2"
                                />
                              ) : (
                                <p className="text-xl text-blue-800 bg-white p-3 rounded border border-gray-200" 
                                   style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}>
                                  {jobData.task_verification.manager_signature}
                                </p>
                              )}
                              <p className="text-xs text-gray-500 mt-2">
                                Signed on: {new Date(jobData.task_verification.verified_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Type/Draw Toggle - only show if not saved */}
                            <div className="flex mb-3 bg-amber-100 rounded-lg p-1">
                              <button
                                type="button"
                                onClick={() => setSignatureMode('type')}
                                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition ${
                                  signatureMode === 'type' 
                                    ? 'bg-white text-amber-800 shadow-sm' 
                                    : 'text-amber-600 hover:text-amber-800'
                                }`}
                              >
                                <Type className="w-4 h-4" />
                                Type
                              </button>
                              <button
                                type="button"
                                onClick={() => setSignatureMode('draw')}
                                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition ${
                                  signatureMode === 'draw' 
                                    ? 'bg-white text-amber-800 shadow-sm' 
                                    : 'text-amber-600 hover:text-amber-800'
                                }`}
                              >
                                <Pen className="w-4 h-4" />
                                Draw
                              </button>
                            </div>
                            
                            {/* Type Signature */}
                            {signatureMode === 'type' && (
                              <div className="relative">
                                <input
                                  type="text"
                                  value={typedSignature}
                                  onChange={(e) => {
                                    setTypedSignature(e.target.value);
                                    setJobData({
                                      ...jobData,
                                      task_verification: {
                                        ...jobData.task_verification,
                                        manager_signature: e.target.value,
                                        requirement_accepted: null
                                      }
                                    });
                                  }}
                                  className="w-full px-4 py-4 border-2 border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white text-2xl text-blue-800"
                                  placeholder="Type your signature here"
                                  style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", color: '#1e40af' }}
                                />
                                {typedSignature && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setTypedSignature('');
                                      setJobData({
                                        ...jobData,
                                        task_verification: {
                                          ...jobData.task_verification,
                                          manager_signature: '',
                                          requirement_accepted: null
                                        }
                                      });
                                    }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            )}
                            
                            {/* Draw Signature */}
                            {signatureMode === 'draw' && (
                              <div className="space-y-2">
                                <div className="relative border-2 border-amber-300 rounded-lg bg-white overflow-hidden" style={{ maxWidth: '100%' }}>
                                  <canvas
                                    ref={canvasRef}
                                    width={350}
                                    height={80}
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                    onTouchStart={startDrawing}
                                    onTouchMove={draw}
                                    onTouchEnd={stopDrawing}
                                    className="w-full cursor-crosshair touch-none"
                                    style={{ touchAction: 'none', maxHeight: '80px' }}
                                  />
                                  <div className="absolute bottom-1 left-3 text-xs text-gray-400 pointer-events-none">
                                    Sign here
                                  </div>
                                  <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-300 mx-4 mb-4"></div>
                                </div>
                                <button
                                  type="button"
                                  onClick={clearCanvas}
                                  className="text-sm text-amber-600 hover:text-amber-800 flex items-center gap-1"
                                >
                                  <RefreshCw className="w-3 h-3" />
                                  Clear signature
                                </button>
                              </div>
                            )}
                            
                            {/* Signature Preview */}
                            {jobData.task_verification?.manager_signature && signatureMode === 'type' && (
                              <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-xs text-gray-500 mb-1">Signature Preview:</p>
                                <p className="text-xl text-blue-800" style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}>
                                  {jobData.task_verification.manager_signature}
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Accept / Deny Buttons - only show when not yet saved */}
                    {jobData.task_verification?.requirement_accepted === null && (
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            // Get signature from canvas if in draw mode
                            let signature = jobData.task_verification?.manager_signature || '';
                            if (signatureMode === 'draw' && canvasRef.current) {
                              signature = canvasRef.current.toDataURL();
                            }
                            
                            if (!jobData.task_verification?.manager_name) {
                              toast.error('Please enter your name first');
                              return;
                            }
                            if (!signature || (signatureMode === 'draw' && signature === 'data:,')) {
                              toast.error('Please provide your signature first');
                              return;
                            }
                            
                            setJobData({
                              ...jobData,
                              task_verification: {
                                ...jobData.task_verification,
                                manager_signature: signature,
                                requirement_accepted: true,
                                verified_at: new Date().toISOString()
                              }
                            });
                            toast.success('Task verification requirement accepted and signature saved permanently');
                          }}
                          className="flex-1 px-4 py-2.5 rounded-lg font-medium transition flex items-center justify-center gap-2 bg-green-500 text-white hover:bg-green-600"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Accept Requirement
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            // Get signature from canvas if in draw mode
                            let signature = jobData.task_verification?.manager_signature || '';
                            if (signatureMode === 'draw' && canvasRef.current) {
                              signature = canvasRef.current.toDataURL();
                            }
                            
                            if (!jobData.task_verification?.manager_name) {
                              toast.error('Please enter your name first');
                              return;
                            }
                            if (!signature || (signatureMode === 'draw' && signature === 'data:,')) {
                              toast.error('Please provide your signature first');
                              return;
                            }
                            
                            setJobData({
                              ...jobData,
                              task_verification: {
                                ...jobData.task_verification,
                                manager_signature: signature,
                                requirement_accepted: false,
                                verified_at: new Date().toISOString()
                              }
                            });
                            toast.info('Task verification requirement denied and signature saved permanently');
                          }}
                          className="flex-1 px-4 py-2.5 rounded-lg font-medium transition flex items-center justify-center gap-2 bg-red-500 text-white hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                          Deny Requirement
                        </button>
                      </div>
                    )}
                    
                    <p className="text-xs text-amber-600 mt-3">
                      * By signing, you acknowledge understanding of the task verification policy for this shift.
                    </p>
                  </div>
                </div>
              </div>

              {/* Dress Code */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Dress Code</label>
                  <input
                    type="text"
                    value={jobData.dress_code}
                    onChange={(e) => setJobData({ ...jobData, dress_code: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 text-gray-900 placeholder-gray-400"
                    placeholder="e.g., Business casual, scrubs..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Equipment Provided</label>
                  <input
                    type="text"
                    value={jobData.equipment_provided}
                    onChange={(e) => setJobData({ ...jobData, equipment_provided: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 text-gray-900 placeholder-gray-400"
                    placeholder="e.g., PPE, tools, uniform..."
                  />
                </div>
              </div>

              {/* Auto-Match */}
              <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl border border-purple-200">
                <input
                  type="checkbox"
                  id="auto_match"
                  checked={jobData.auto_match}
                  onChange={(e) => setJobData({ ...jobData, auto_match: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600"
                />
                <div>
                  <label htmlFor="auto_match" className="font-medium text-purple-900">Enable AI Auto-Matching</label>
                  <p className="text-sm text-purple-700">Automatically match and suggest qualified workers for this job</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{jobData.title || 'Untitled Job'}</h3>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Tag className="w-4 h-4" />
                    <span className="capitalize">{jobData.category}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <DollarSign className="w-4 h-4" />
                    <span>${jobData.hourly_rate || jobData.fixed_amount || '0'}{jobData.pay_type === 'hourly' ? '/hr' : ' total'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>{jobData.start_date || 'No date set'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>{jobData.workers_needed} worker(s) needed</span>
                  </div>
                </div>

                <p className="text-gray-700 mb-4">{jobData.description || 'No description provided'}</p>

                {jobData.required_skills.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-500 mb-2">Required Skills:</p>
                    <div className="flex flex-wrap gap-2">
                      {jobData.required_skills.map((skill, idx) => (
                        <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {jobData.required_certifications.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Required Certifications:</p>
                    <div className="flex flex-wrap gap-2">
                      {jobData.required_certifications.map((cert, idx) => (
                        <span key={idx} className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm flex items-center gap-1">
                          <Award className="w-3 h-3" />
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation — always visible; body scrolls above it */}
        <div className="border-t border-gray-200 p-3 sm:p-4 bg-gray-50 flex flex-wrap items-center gap-2 sm:gap-3 flex-shrink-0" data-testid="job-wizard-footer">
          <button
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="px-4 sm:px-6 py-2.5 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition disabled:opacity-50 flex items-center gap-2"
            data-testid="job-wizard-back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-gray-600 rounded-xl font-medium hover:bg-gray-200 transition"
            data-testid="job-wizard-cancel-btn"
          >
            Cancel
          </button>
          <div className="flex-1" />
          {step < 4 ? (
            <button
              onClick={() => goToStep(step + 1)}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition flex items-center gap-2"
              data-testid="job-wizard-next-btn"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <>
              <button
                onClick={handleSaveDraft}
                disabled={loading}
                data-testid="save-draft-btn"
                className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition flex items-center justify-center gap-2 border border-gray-200"
              >
                <Save className="w-5 h-5" />
                Save as Draft
              </button>
              <button
                onClick={handleSaveTemplate}
                data-testid="save-template-btn"
                className="px-4 py-2.5 bg-purple-100 text-purple-700 rounded-xl font-semibold hover:bg-purple-200 transition flex items-center gap-2"
              >
                <Copy className="w-5 h-5" />
                Save Template
              </button>
              <button
                onClick={handlePublish}
                disabled={loading}
                data-testid="publish-job-btn"
                className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-semibold hover:shadow-lg transition flex items-center justify-center gap-2"
              >
                {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                Publish Job
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
